import React from 'react';

import { Pagination, Radio } from 'antd';

import type { ChartView } from '../types';
import styles from './styles.module.less';

export interface ChartViewLabels {
  ariaLabel: string;
  bar: string;
  line: string;
}

export interface ChartViewToggleProps {
  value: ChartView;
  labels: ChartViewLabels;
  onChange: (view: ChartView) => void;
}

/** Switches a chart between bar and line/area rendering. */
export const ChartViewToggle: React.FC<ChartViewToggleProps> = ({ value, labels, onChange }) => (
  <Radio.Group
    aria-label={labels.ariaLabel}
    size="small"
    buttonStyle="solid"
    value={value}
    onChange={event => onChange(event.target.value)}
  >
    <Radio.Button value="bar">{labels.bar}</Radio.Button>
    <Radio.Button value="line">{labels.line}</Radio.Button>
  </Radio.Group>
);

export interface ChartPagerProps {
  /** Zero-based page. */
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
  ariaLabel: string;
  onChange: (page: number) => void;
}

/** Compact pager, rendered only when the series spans more than one page. */
export const ChartPager: React.FC<ChartPagerProps> = ({
  page,
  pageSize,
  pageCount,
  total,
  ariaLabel,
  onChange,
}) => {
  if (pageCount <= 1) return null;

  return (
    <Pagination
      aria-label={ariaLabel}
      current={page + 1}
      pageSize={pageSize}
      simple
      showSizeChanger={false}
      total={total}
      onChange={nextPage => onChange(nextPage - 1)}
    />
  );
};

export interface ChartControlsProps {
  view: ChartViewToggleProps;
  pager: ChartPagerProps;
}

/** Header controls for a paginated chart: view toggle plus pager. */
const ChartControls: React.FC<ChartControlsProps> = ({ view, pager }) => (
  <div className={styles.controls}>
    <ChartViewToggle {...view} />
    <ChartPager {...pager} />
  </div>
);

export default ChartControls;
